import { merge, times } from 'lodash';

import { useRegistrationFlowContext } from 'hb-react/shared/apps/Events/contexts/RegistrationFlowContext';
import { getTicketInformation } from 'hb-react/shared/apps/Events/utils/getTicketInformation';

export const useGetInitialValuesStep1 = ({ tickets = [] }) => {
  const { valuesStep1 } = useRegistrationFlowContext();

  return {
    tickets: tickets.map(({ id, isFree, currency }) => ({
      id,
      isFree,
      currency,
      quantity: 0,
      ...valuesStep1.tickets.find((ticket) => id === ticket.id),
    })),
  };
};

const initTickets = ({ tickets, attendees = [], event }) =>
  tickets.reduce((result, ticketChosen) => {
    const potentialAttendees = attendees.filter(
      ({ ticket_id: id, email }) => id === ticketChosen.id && email,
    );

    const { addons } = getTicketInformation(event, ticketChosen.id);

    times(ticketChosen.quantity, () => {
      const defaults = {
        ticket_id: ticketChosen.id,
        isFree: ticketChosen.isFree,
        user_id: null,
        firstname: '',
        lastname: '',
        email: '',
        company_name: '',
        mobile_phone: '',
        custom_field_1: '',
        custom_field_2: '',
        custom_field_3: '',
        hidden: false,
        customAttributes: [],
        addons_attributes: addons.map((addon) => ({
          ...addon,
          quantity: 0,
        })),
      };

      const attendee =
        potentialAttendees.length > 0 ? potentialAttendees.shift() : [];

      return result.push({
        ...defaults,
        ...attendee,
      });
    });

    return result;
  }, []);

export const useGetInitialValuesStep2 = (event) => {
  const { valuesStep1, valuesStep2 } = useRegistrationFlowContext();

  const attendees = valuesStep2?.network_events_booking?.attendees_attributes;

  const tickets = valuesStep1.tickets.filter((t) => t.quantity > 0);

  const attendeesAttributes = initTickets({
    tickets,
    attendees,
    event,
  });
  const addonsAttributes =
    valuesStep2?.network_events_booking?.addons_attributes ||
    event.addons.filter(({ standalone }) => standalone);

  const donationsAttributes =
    valuesStep2?.network_events_booking?.donations_attributes || [];

  return {
    network_events_booking: {
      attendees_attributes: attendeesAttributes,
      addons_attributes: addonsAttributes,
      donations_attributes: donationsAttributes,
    },
  };
};

export const getInitialValuesStep3 = (
  { currentUser, values, isShippable },
  { selectedAddress } = { selectedAddress: 'personal' },
) => {
  const address = currentUser?.postal_address?.[selectedAddress];

  const shippingAddressAttributes = {
    same_as_billing: true,
    full_name: currentUser?.name,
    address_1: address?.address_1 || '',
    address_2: address?.address_2 || '',
    address_3: address?.address_3 || '',
    country: address?.country || '',
    city: address?.city || '',
    postal_code: address?.postal_code || '',
    state: address?.state || '',
    recaptcha_response: '',
  };

  return merge(
    {
      receipt: {
        firstname: currentUser?.firstname || '',
        lastname: currentUser?.lastname || '',
        email: currentUser?.email || '',
        address_type: selectedAddress,
        billing_address_attributes: {
          address_1: address?.address_1 || '',
          address_2: address?.address_2 || '',
          address_3: address?.address_3 || '',
          country: address?.country || '',
          city: address?.city || '',
          postal_code: address?.postal_code || '',
          state: address?.state || '',
        },
        shipping_address_attributes: isShippable && shippingAddressAttributes,
      },
    },
    values,
  );
};

export const useGetInitialValuesDonations = ({ donations }) => {
  const { valuesStep2 } = useRegistrationFlowContext();

  return {
    donations: donations.map((donation) => ({
      ...donation,
      amount: '',
      ...valuesStep2?.network_events_booking?.donations_attributes.find(
        ({ id }) => id === donation.id,
      ),
    })),
  };
};
